import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, ChevronLeft, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * DistributorWeeklyCard — shows roll-up + per-operator weekly activity
 * for the logged-in distributor. Read-only view; settlements are admin-only.
 */
const DistributorWeeklyCard = () => {
    const [cycle, setCycle] = useState('current');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/distributor/weekly`, {
                params: { cycle }, withCredentials: true,
            });
            setData(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [cycle]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return (
        <div className="card-dark p-6 text-center text-[#A0AAB2]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading week…
        </div>
    );
    if (!data) return null;

    const r = data.rollup;
    const start = new Date(data.cycle_start);
    const end = new Date(data.cycle_end);
    const opts = { month: 'short', day: 'numeric' };
    const range = `${start.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)} (CT)`;

    return (
        <div className="card-dark p-6 mb-6" data-testid="distributor-weekly-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h2 className="text-xl font-bold text-white">Network — This Week</h2>
                    <p className="text-xs text-[#A0AAB2] mt-1">{data.cycle} · {range}</p>
                </div>
                <Tabs value={cycle} onValueChange={setCycle}>
                    <TabsList className="bg-[#0a0a0a] border border-white/10">
                        <TabsTrigger value="prev"><ChevronLeft className="w-3 h-3 mr-1" />Last week</TabsTrigger>
                        <TabsTrigger value="current">This week</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Rollup */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Tile label="Operators" value={r.operator_count} color="text-white" />
                <Tile label="Loaded" value={fmt(r.loaded)} color="text-[#39FF14]" />
                <Tile label="Cashed" value={fmt(r.cashed)} color="text-[#FFD700]" />
                <Tile label="Wagered" value={fmt(r.wagered)} color="text-white" />
                <Tile label="Net house" value={fmt(r.net_house)} color={r.net_house >= 0 ? 'text-[#00E0FF]' : 'text-[#FF3B30]'} highlight />
            </div>

            {/* Per-operator */}
            {data.operators?.length > 0 && (
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">Operators</p>
                    <div className="space-y-1">
                        {data.operators.map(o => (
                            <div key={o.id} className="flex items-center justify-between bg-[#0a0a0a] rounded-md px-3 py-2 text-sm" data-testid={`dist-op-${o.id}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-white font-bold">{o.name}</span>
                                    {o.settlement?.settled && (
                                        <span className="text-[10px] uppercase font-bold text-[#39FF14] bg-[#39FF14]/10 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" />Settled
                                        </span>
                                    )}
                                </div>
                                <span className="font-mono text-[#A0AAB2]">
                                    <span className="text-[#39FF14]">{fmt(o.totals.loaded)}</span> in · <span className="text-[#FFD700]">{fmt(o.totals.cashed)}</span> out · net <span className={o.totals.net_house >= 0 ? 'text-[#00E0FF]' : 'text-[#FF3B30]'}>{fmt(o.totals.net_house)}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tile = ({ label, value, color, highlight }) => (
    <div className={`rounded-lg p-3 border ${highlight ? 'border-[#00E0FF]/40 bg-[#00E0FF]/5' : 'border-white/10 bg-[#0a0a0a]'}`}>
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">{label}</p>
        <p className={`text-xl font-mono font-black ${color}`}>{value}</p>
    </div>
);

export default DistributorWeeklyCard;
